import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { isItemActive, visibleNav, type NavChild } from "@/config/nav";

/**
 * The About Us dropdown in the landing nav.
 *
 * Items come from `@/config/nav`, not from a list kept here. That file is
 * already the single source of truth for the header and both footers, and it
 * carries the feature flags — Careers is behind FEATURES.jobs and Forum behind
 * FEATURES.forum — so consuming it means this menu cannot drift from the rest
 * of the site or show a route that is switched off.
 *
 * These are the only real routes in this nav. Every other link in the design is
 * an in-page anchor, so the four children below use react-router `Link` while
 * their siblings stay plain `<a href="#...">`.
 *
 * Interaction follows the header's existing menu rather than inventing one:
 * hover opens on a pointer that can hover, click toggles for one that cannot,
 * and a close delay plus a padding hover-bridge lets the pointer travel from
 * the trigger down into the panel without the gap dismissing it. The three
 * dismissals the old hand-rolled card was missing — Escape, outside click, and
 * route change — are all wired up here.
 */

/** Matches the 150ms the site header uses, so the two menus feel the same. */
const CLOSE_DELAY_MS = 150;

/** The gap between bar and panel, as padding so the pointer never leaves. */
const BRIDGE_PX = 10;

const NAV_HEIGHT = 68;

export default function AboutMenu() {
  const { pathname } = useLocation();
  const panelId = useId();

  const [open, setOpen] = useState(false);
  /**
   * Whether the panel is held open by an explicit click rather than by the
   * pointer merely resting on the trigger.
   *
   * Without this, hover and click fight each other: moving the pointer to the
   * trigger opens the panel, so the click that follows sees an already-open
   * menu and a blind toggle closes it again — the panel flashes and the click
   * reads as broken. Hover previews, a click pins, and a second click
   * dismisses.
   */
  const [pinned, setPinned] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);

  const dismiss = useCallback(() => {
    setOpen(false);
    setPinned(false);
  }, []);

  const item = visibleNav().find((i) => i.label === "About Us");
  const children: NavChild[] = item?.children ?? [];

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== undefined) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    // A pinned panel ignores the pointer leaving; only a click, Escape, an
    // outside press or a navigation closes it.
    if (pinned) return;
    cancelClose();
    closeTimer.current = window.setTimeout(
      () => setOpen(false),
      CLOSE_DELAY_MS,
    );
  }, [cancelClose, pinned]);

  useEffect(() => cancelClose, [cancelClose]);

  // Navigating away must dismiss the panel. Without this the menu stays open
  // over the page the visitor just asked for.
  useEffect(() => {
    dismiss();
  }, [pathname, dismiss]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      dismiss();
      // Send focus back to the trigger rather than letting it fall to the top
      // of the document, which is where Escape would otherwise strand it.
      triggerRef.current?.focus();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) dismiss();
    };
    // Focus leaving the group dismisses it too, so tabbing past the last child
    // closes the panel instead of leaving it hanging open behind the page.
    const onFocusIn = (e: FocusEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) dismiss();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [open, dismiss]);

  if (!item) return null;

  const parentActive = isItemActive(item, pathname);

  return (
    <div
      ref={wrapRef}
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          // Hover may already have opened it; pin rather than toggle shut.
          if (!open) {
            setOpen(true);
            setPinned(true);
          } else if (!pinned) {
            setPinned(true);
          } else {
            dismiss();
          }
        }}
        className="dc-h-ink"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          fontFamily: "'Manrope', sans-serif",
          fontSize: 13.5,
          fontWeight: 500,
          whiteSpace: "nowrap",
          color: parentActive ? "var(--ink)" : "rgba(var(--ink-rgb),.78)",
        }}
      >
        {item.label}
        {/* Rotates on open. The header's version never did — its rotate hook
            was keyed to a `group` class that was not on the element. */}
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            fontSize: 9,
            lineHeight: 1,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform .2s",
          }}
        >
          &#9660;
        </span>
      </button>

      {open && (
        <div
          id={panelId}
          // Tagged so the stacked mobile nav can drop the floating position and
          // let this flow inline; see `.dc-aboutpanel` in styles.ts.
          className="dc-aboutpanel"
          style={{
            position: "absolute",
            top: NAV_HEIGHT / 2 + 12,
            left: "50%",
            transform: "translateX(-50%)",
            paddingTop: BRIDGE_PX,
            zIndex: 70,
          }}
        >
          <div
            style={{
              width: 320,
              background: "var(--paper2)",
              border: "1px solid rgba(var(--ink-rgb),.13)",
              borderRadius: 18,
              boxShadow: "0 24px 60px rgba(9,9,7,.18)",
              padding: 10,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {children.map((child) => {
              const active = pathname === child.to;
              return (
                <Link
                  key={child.to}
                  to={child.to}
                  aria-current={active ? "page" : undefined}
                  onClick={dismiss}
                  className="dc-h-menurow"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "11px 14px",
                    borderRadius: 12,
                    border: `1px solid ${active ? "rgba(var(--ink-rgb),.16)" : "transparent"}`,
                    background: active
                      ? "rgba(var(--ink-rgb),.05)"
                      : "transparent",
                    color: "var(--ink)",
                    transition:
                      "border-color .2s, background .2s, color .2s",
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: 14.5,
                        fontWeight: 600,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {child.label}
                    </span>
                    {child.hint && (
                      <span
                        style={{
                          display: "block",
                          marginTop: 2,
                          fontSize: 12.5,
                          fontWeight: 400,
                          lineHeight: 1.4,
                          color: "rgba(var(--ink-rgb),.62)",
                        }}
                      >
                        {child.hint}
                      </span>
                    )}
                  </span>
                  <span
                    aria-hidden="true"
                    style={{
                      marginLeft: "auto",
                      fontFamily: "'Manrope', sans-serif",
                      fontSize: 11,
                      color: active
                        ? "var(--acc2)"
                        : "rgba(var(--ink-rgb),.25)",
                    }}
                  >
                    &rarr;
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
